import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  deleteContact,
  getContactById,
  getContacts,
  rappelerDocuments,
  apercuRappelDocuments,
  ajouterHistorique,
} from '../services/contactService';
import ApercuEmail from './ApercuEmail';
import GenererSuivant from '../components/GenererSuivant';
import {
  genererDevis,
  getDevisParContact,
  regenererPdfDevis,
  telechargerPdfDevis,
  urlPdfDevis,
} from '../services/devisService';
import PanneauApercu from './PanneauApercu';
import ContactDetailPanel from './ContactDetailPanel';
import DevisPanel from './DevisPanel';
import DevisManuelPanel from './DevisManuelPanel';
import NouvelleDemandePanel from './NouvelleDemandePanel';
import ImportTrelloPanel from './ImportTrelloPanel';
import { STATUT_LABELS, formatDate, formatDateTime, formaterTelephone } from './devisUtils';
import Avancement from './Avancement';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import BoutonIcone, { IconeEnveloppe, IconeImport, IconePlus, IconeDepuisTexte } from './BoutonIcone';
import CreerDepuisTextePanel from './CreerDepuisTextePanel';
import BoutonExport from './BoutonExport';
import StatutsBarre from './StatutsBarre';
import Notification from '../components/Notification';
import ActionsLigne from '../components/ActionsLigne';
import { FACETTES_DEMANDE, FACETTES_ORIGINE } from './facettes';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/DemandeDevis.css';
import { IconeOeil, IconeTelecharger } from '../components/IconesDocument';

const LIMIT = 10;

/** Ce qui nomme une demande à l'écran : le contact, sinon la société, sinon la carte. */
function nomDemande(contact) {
  return (
    [contact.prenom, contact.nom].filter(Boolean).join(' ') ||
    contact.societe_nom ||
    contact.trello_card_nom ||
    `Demande n° ${contact.id}`
  );
}

/**
 * Teinte de la pastille « reçues / attendues » : rouge tant qu'une pièce
 * obligatoire manque, verte quand tout est là, neutre entre les deux (il ne
 * reste que du facultatif).
 */
function classeDocuments(contact) {
  if (contact.nb_documents_manquants > 0) return 'devis-docs-manquant';
  if (contact.nb_documents_recus >= contact.nb_documents_attendus) return 'devis-docs-complet';
  return 'devis-docs-partiel';
}

/** Libellés de la barre de tri : les en-têtes de colonnes n'existent plus. */
const LIBELLES_TRI = {
  nom: 'Contact',
  societe: 'Société',
  statut: 'État',
  date: 'Date de réception',
};

const COLUMNS = [
  { key: 'nom', label: 'CONTACT' },
  { key: 'societe', label: 'SOCIÉTÉ · OBJET' },
  { key: 'statut', label: 'ÉTAT' },
  { key: 'date', label: 'REÇU LE' },
];

function DemandeDevis() {
  const [contacts, setContacts] = useState([]);
  // Réglage de l'email propre à cette page (cf. ModeleEmailPanneau).
  const [panneauEmail, setPanneauEmail] = useState(false);
  const [total, setTotal] = useState(0);
  // Nombre de demandes par statut, tous statuts confondus : alimente les
  // pastilles de filtre au-dessus de la table.
  const [compteurs, setCompteurs] = useState({});
  const [compteursOrigine, setCompteursOrigine] = useState({});
  // Interne (généré ici) ou externe (établi ailleurs, puis téléversé).
  const [origine, setOrigine] = useState('');
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('DESC');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // ?contact=ID (depuis l'historique du reporting) : la fiche s'ouvre
  // directement, sans la chercher dans la liste.
  const [params] = useSearchParams();
  const contactCible = Number(params.get('contact')) || null;
  useEffect(() => {
    if (contactCible) setSelectedId(contactCible);
  }, [contactCible]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Devis rattaché à la demande ouverte dans le panneau de détail.
  const [devisContact, setDevisContact] = useState(null);
  const [devisLoading, setDevisLoading] = useState(false);
  const [devisOuvert, setDevisOuvert] = useState(null);
  // Ouvert depuis « Modifier » : le devis s'ouvre directement sur son formulaire.
  const [devisEnEdition, setDevisEnEdition] = useState(false);
  // Demande en cours de classement « sans suite ».
  const [sansSuiteId, setSansSuiteId] = useState(null);

  // Génération depuis la liste : id en cours, et message par ligne.
  const [generationId, setGenerationId] = useState(null);
  // Ouverture de l'aperçu d'un devis déjà généré, depuis la liste.
  const [ouvertureId, setOuvertureId] = useState(null);
  const [generationErreur, setGenerationErreur] = useState(null);
  const [reload, setReload] = useState(0);

  // Ouvert quand la génération IA échoue faute de formation exploitable (422).
  const [saisieManuelle, setSaisieManuelle] = useState(null);
  const [nouvelleDemande, setNouvelleDemande] = useState(false);
  // « Créer depuis un texte » : l'IA lit, on relit et corrige, puis on crée.
  const [depuisTexte, setDepuisTexte] = useState(false);

  // Import Trello : en cours, et résumé (ou erreur) affiché à l'arrivée.
  // Corriger une demande sans devis, et relancer sur les pièces à fournir :
  // deux gestes conditionnels — l'un n'a de sens qu'avant le devis, l'autre
  // qu'avec des pièces encore attendues.
  const [demandeModifiee, setDemandeModifiee] = useState(null);
  const [rappelId, setRappelId] = useState(null);
  const [rappelMessage, setRappelMessage] = useState(null);

  const [importOuvert, setImportOuvert] = useState(false);

  // Suivi de la génération automatique déclenchée à la création d'une demande.
  const [attente, setAttente] = useState(null);

  // Aperçu du devis : le document tel qu'il part, sans quitter la liste —
  // le même panneau que dans la liste des devis. `apercuDevis` est la
  // demande dont on regarde le devis ; `pdfId` le téléchargement en cours.
  const [apercuDevis, setApercuDevis] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  // « Régénérer le PDF » depuis l'aperçu : le cadre se recharge (paramètre v).
  const [regenId, setRegenId] = useState(null);
  const [versionApercu, setVersionApercu] = useState(0);

  async function regenererPdf(contact) {
    setRegenId(contact.devis_id);
    try {
      await regenererPdfDevis(contact.devis_id);
      setVersionApercu((v) => v + 1);
      setReload((n) => n + 1);
    } catch (err) {
      setSuppressionMessage({
        type: 'erreur',
        titre: 'Régénération impossible',
        texte: err.message || 'Régénération impossible.',
      });
    } finally {
      setRegenId(null);
    }
  }

  async function handlePdfDevis(devisId) {
    setPdfId(devisId);
    try {
      await telechargerPdfDevis(devisId);
      // Le téléchargement renseigne pdf_path : la ligne doit refléter la pièce.
      setReload((n) => n + 1);
    } catch (err) {
      setSuppressionMessage({
        type: 'erreur',
        titre: 'Téléchargement impossible',
        texte: err.message || 'Téléchargement impossible.',
      });
    } finally {
      setPdfId(null);
    }
  }

  // Suppression d'une demande : celle qu'on s'apprête à effacer (la
  // confirmation est ouverte), l'appel en cours, et le compte rendu.
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [suppressionMessage, setSuppressionMessage] = useState(null);

  /** Met la demande confirmée à la corbeille, avec tout ce qui en dépend. */
  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      await deleteContact(cible.id);
      setSuppressionMessage({
        type: 'succes',
        texte: `${nomDemande(cible)} mise à la corbeille (restaurable 30 jours). Ses devis, contrats et documents restent.`,
      });
      setSuppression(null);
      // La fiche ouverte sur cette demande n'a plus rien à montrer.
      if (selectedId === cible.id) setSelectedId(null);
      setReload((n) => n + 1);
    } catch (err) {
      setSuppressionMessage({
        type: 'erreur',
        texte: err.message || 'Suppression impossible.',
      });
    } finally {
      setSuppressionEnCours(false);
    }
  }

  // La recherche part au serveur : on attend une pause de frappe pour ne pas
  // déclencher une requête par caractère.
  useEffect(() => {
    const timer = setTimeout(() => {
      setRecherche(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    getContacts({ page, limit: LIMIT, search: recherche, statut, origine, dateDebut, dateFin, sortBy, sortDir })
      .then((res) => {
        if (cancelled) return;
        setContacts(res.data || []);
        setTotal(res.pagination?.total ?? (res.data || []).length);
        setCompteurs(res.compteurs || {});
        setCompteursOrigine(res.compteursOrigine || {});
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger les demandes de devis.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, recherche, statut, origine, dateDebut, dateFin, sortBy, sortDir, reload]);

  useEffect(() => {
    if (selectedId == null) return;

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setSelectedContact(null);
    setDevisContact(null);
    setDevisLoading(true);

    getContactById(selectedId)
      .then((res) => {
        if (!cancelled) setSelectedContact(res.data);
      })
      .catch(() => {
        if (!cancelled) setDetailError('Impossible de charger le détail de cette demande.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    // Le devis est chargé en parallèle : son absence n'est pas une erreur.
    getDevisParContact(selectedId)
      .then((res) => {
        if (!cancelled) setDevisContact(res.data);
      })
      .catch(() => {
        if (!cancelled) setDevisContact(null);
      })
      .finally(() => {
        if (!cancelled) setDevisLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  /**
   * Le serveur génère le devis en tâche de fond dès la création d'une demande.
   * On interroge jusqu'à ce qu'il apparaisse, ou jusqu'à ce que l'historique
   * signale que la génération a échoué — auquel cas on propose la saisie
   * manuelle avec le motif.
   */
  useEffect(() => {
    if (!attente || attente.etat !== 'encours') return;

    let annule = false;
    let tentatives = 0;
    const MAX = 25; // ~50 s : au-delà, l'appel au modèle a manifestement échoué

    const timer = setInterval(async () => {
      if (annule) return;
      tentatives += 1;

      try {
        const res = await getDevisParContact(attente.contact.id);
        if (annule) return;

        if (res.data) {
          clearInterval(timer);
          setAttente(null);
          setReload((n) => n + 1);
          setDevisContact(res.data);
          setDevisOuvert(res.data);
          return;
        }

        // Pas de devis : la génération a-t-elle consigné un échec ?
        const detail = await getContactById(attente.contact.id);
        if (annule) return;

        const echec = [...(detail.data.historique || [])]
          .sort((a, b) => new Date(b.dateMaj) - new Date(a.dateMaj))
          .find((h) => (h.commentaire || '').startsWith('Génération automatique impossible'));

        if (echec) {
          clearInterval(timer);
          setReload((n) => n + 1);
          setAttente({
            ...attente,
            etat: 'echec',
            motif: echec.commentaire.replace('Génération automatique impossible : ', ''),
          });
          return;
        }
      } catch {
        /* erreur réseau ponctuelle : on retentera au tour suivant */
      }

      if (tentatives >= MAX && !annule) {
        clearInterval(timer);
        setAttente({
          ...attente,
          etat: 'echec',
          motif: "La génération n'a pas abouti dans le délai attendu.",
        });
      }
    }, 2000);

    return () => {
      annule = true;
      clearInterval(timer);
    };
  }, [attente]);

  /**
   * Ouvre l'aperçu du devis déjà généré d'une demande : c'est là que la
   * validation se fait. La liste ne porte que l'id du devis, on recharge donc
   * le devis complet avant d'ouvrir le panneau.
   */
  async function handleOuvrirDevis(contactId, enEdition = false) {
    setOuvertureId(contactId);
    setGenerationErreur(null);
    setDevisEnEdition(enEdition);

    try {
      const res = await getDevisParContact(contactId);
      if (!res.data) {
        setGenerationErreur({ contactId, message: 'Devis introuvable.' });
        return;
      }
      if (selectedId === contactId) setDevisContact(res.data);
      setDevisOuvert(res.data);
    } catch (err) {
      setGenerationErreur({
        contactId,
        message: err.message || 'Ouverture du devis impossible.',
      });
    } finally {
      setOuvertureId(null);
    }
  }

  /** Clôt la demande : écrit une entrée d'historique « sans suite ». */
  async function handleSansSuite(contact) {
    setSansSuiteId(contact.id);
    try {
      await ajouterHistorique(contact.id, {
        statut: 'sans_suite',
        commentaire: 'Demande classée sans suite depuis la liste des demandes.',
      });
      setRappelMessage({
        type: 'succes',
        texte: `Demande de ${nomDemande(contact)} classée sans suite.`,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setRappelMessage({
        type: 'erreur',
        titre: 'Classement impossible',
        texte: err.message || 'Enregistrement impossible.',
      });
    } finally {
      setSansSuiteId(null);
    }
  }

  /** Génère le devis d'une demande. La demande passe « traité » côté serveur. */
  // L'aperçu du rappel de pièces avant l'envoi : le contact concerné.
  const [apercuRappel, setApercuRappel] = useState(null);

  /** Ouvre l'aperçu du rappel ; rien ne part avant le clic dans la modale. */
  function handleRappelDocuments(contact) {
    setRappelMessage(null);
    setApercuRappel(contact);
  }

  /** L'envoi réel du rappel, confirmé depuis l'aperçu. */
  async function envoyerRappelApresApercu(contact, { sujet, texte }) {
    setRappelId(contact.id);
    try {
      const res = await rappelerDocuments(contact.id, { sujet, texte });
      setRappelMessage({ type: 'succes', texte: res.message });
    } finally {
      setRappelId(null);
    }
  }

  async function handleGenererDevis(contactId) {
    setGenerationId(contactId);
    setGenerationErreur(null);

    try {
      const res = await genererDevis(contactId);
      setReload((n) => n + 1);
      if (selectedId === contactId) setDevisContact(res.data);
      setDevisOuvert(res.data);
    } catch (err) {
      // 422 = demande comprise, mais rien de facturable dedans.
      // On bascule sur la saisie manuelle plutôt que de laisser l'utilisateur
      // devant une impasse.
      if (err.status === 422) {
        setSaisieManuelle({
          contact: contacts.find((c) => c.id === contactId) || { id: contactId },
          raison: err.message,
        });
      } else {
        setGenerationErreur({
          contactId,
          message: err.message || 'Génération impossible.',
        });
      }
    } finally {
      setGenerationId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const rangeStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const rangeEnd = Math.min(page * LIMIT, total);
  const filtreActif = Boolean(statut || origine || dateDebut || dateFin || recherche.trim());

  // Total de la pastille « Toutes » : la somme des compteurs, pas `total`, qui
  // ne compte que les demandes du statut sélectionné.
  const totalTousStatuts = Object.values(compteurs).reduce((s, n) => s + (Number(n) || 0), 0);

  function handleSort(columnKey) {
    if (sortBy === columnKey) {
      setSortDir((dir) => (dir === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(columnKey);
      setSortDir('ASC');
    }
    setPage(1);
  }

  function handleFiltre(setter) {
    return (e) => {
      setter(e.target.value);
      setPage(1);
    };
  }

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Demandes de devis</h1>
        </div>
        <div className="devis-header-actions">
          <BoutonExport
            chemin="/contacts/export"
            filtres={{ statut, origine, search: recherche, dateDebut, dateFin, sortBy, sortDir }}
          />
          <BoutonIcone
            libelle="Paramétrage email"
            onClick={() => setPanneauEmail(true)}
          >
            <IconeEnveloppe />
          </BoutonIcone>
          <BoutonIcone
            libelle="Importer depuis Trello"
            onClick={() => setImportOuvert(true)}
          >
            <IconeImport />
          </BoutonIcone>
          <BoutonIcone
            libelle="Créer une demande depuis un texte (l’IA lit, vous relisez)"
            onClick={() => setDepuisTexte(true)}
          >
            <IconeDepuisTexte />
          </BoutonIcone>
          <BoutonIcone
            libelle="Nouvelle demande"
            onClick={() => setNouvelleDemande(true)}
            primaire
          >
            <IconePlus />
          </BoutonIcone>
        </div>
      </div>

      <FiltresRepliables libelle="Filtres">
        <div className="devis-toolbar">
          <input
            type="text"
            className="devis-search"
            placeholder="Rechercher un nom, une société..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="devis-date-filter">
            <div className="devis-periode">
              <label className="devis-date-field">
                <span>Du</span>
                <input type="date" value={dateDebut} onChange={handleFiltre(setDateDebut)} max={dateFin || undefined} />
              </label>
              <label className="devis-date-field">
                <span>Au</span>
                <input type="date" value={dateFin} onChange={handleFiltre(setDateFin)} min={dateDebut || undefined} />
              </label>
            </div>
            {filtreActif && (
              <button
                type="button"
                className="devis-date-clear"
                onClick={() => {
                  setSearch('');
                  setStatut('');
                  setOrigine('');
                  setDateDebut('');
                  setDateFin('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </button>
            )}
          </div>
          <span className="devis-total">{total} demande{total > 1 ? 's' : ''}</span>
        </div>

        <StatutsBarre
          titre="Statut"
          options={FACETTES_DEMANDE}
          compteurs={compteurs}
          valeur={statut}
          total={totalTousStatuts}
          onSelect={(valeur) => {
            setStatut(valeur);
            setPage(1);
          }}
        />
        {/* Interne : généré par l'application ; externe : établi hors de
            l'application puis téléversé. Se combine avec les autres pastilles. */}
        <StatutsBarre
          titre="Origine"
          options={FACETTES_ORIGINE}
          compteurs={compteursOrigine}
          valeur={origine}
          total={Object.values(compteursOrigine).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Toutes"
          onSelect={(v) => {
            setOrigine(v);
            setPage(1);
          }}
        />
      </FiltresRepliables>


      <div className="devis-card">

        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des demandes...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && contacts.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucune demande ne correspond à cette vue.</div>
              <div className="devis-empty-subtitle">Les nouvelles demandes de devis apparaîtront ici.</div>
            </div>
          )}

          {!loading && !error && contacts.length > 0 && (
            <div className="demande-liste">
              {/* Le tri passait par les en-têtes de colonnes : la liste n'en
                  a plus, la barre ci-dessous reprend les mêmes clés. */}
              <div className="demande-tri" role="group" aria-label="Trier les demandes">
                <span className="demande-tri-label">Trier par</span>
                {COLUMNS.map((col) => (
                  <button
                    key={col.key}
                    type="button"
                    className={`demande-tri-btn${sortBy === col.key ? ' demande-tri-actif' : ''}`}
                    onClick={() => handleSort(col.key)}
                  >
                    {LIBELLES_TRI[col.key] || col.label}
                    <span className={`devis-sort-icon${sortBy === col.key ? ' devis-sort-icon-active' : ''}`}>
                      {sortBy === col.key ? (sortDir === 'ASC' ? '↑' : '↓') : '↕'}
                    </span>
                  </button>
                ))}
              </div>

              {contacts.map((contact) => (
                <article
                  key={contact.id}
                  className="demande-carte"
                  onClick={() => setSelectedId(contact.id)}
                >
                  <div className="demande-carte-corps">
                    {/* Une seule colonne pour qui, quelle société, quel
                        objet : on lit la demande d'un trait, de haut en bas. */}
                    <div className="demande-carte-identite">
                      <div className="demande-numero-ligne">
                        {contact.devis_numero ? (
                          <span
                            className={`demande-numero${
                              contact.devis_valide_le ? ' demande-numero-valide' : ''
                            }`}
                            title={
                              contact.devis_valide_le
                                ? `Devis ${contact.devis_numero} — validé`
                                : `Devis ${contact.devis_numero} — à valider`
                            }
                          >
                            {contact.devis_numero}
                          </span>
                        ) : (
                          <span className="demande-numero demande-numero-vide">
                            Pas encore de devis
                          </span>
                        )}
                        {contact.devis_origine === 'externe' && (
                          <span
                            className="badge-origine badge-origine-externe"
                            title="Devis établi hors de l’application, puis téléversé ici"
                          >
                            Externe
                          </span>
                        )}
                        {contact.contrat_origine === 'externe' && (
                          <span
                            className="badge-origine badge-origine-externe"
                            title="Contrat établi hors de l’application, puis téléversé (Parapheur)"
                          >
                            Contrat externe
                          </span>
                        )}
                      </div>

                      <div className="devis-contact-name">
                        {contact.prenom} {contact.nom}
                      </div>
                      <div className="demande-carte-coordonnees">
                        {contact.email && <span>{contact.email}</span>}
                        {contact.numero_telephone && (
                          <a
                            href={`tel:${String(contact.numero_telephone).replace(/\s+/g, '')}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formaterTelephone(contact.numero_telephone)}
                          </a>
                        )}
                      </div>

                      <div className="demande-carte-societe">{contact.societe_nom || '—'}</div>
                      {/* Un bloc, et non le texte à nu : `max-height` n'est pas
                          honoré sur une cellule de tableau. */}
                      <div className="devis-status-detail texte-borne">
                        {contact.objet_message || '—'}
                      </div>

                    </div>

                    <div className="demande-carte-etat">
                      {/* La même teinte que la facette du statut, au-dessus de
                          la liste : bleu pour « à traiter », rouge pour « à
                          générer », vert pour « terminé »… */}
                      <span
                        className={`devis-status-pill statut-${contact.dernier_statut || 'a_traiter'}`}
                      >
                        {STATUT_LABELS[contact.dernier_statut] || 'À traiter'}
                      </span>
                      <div className="devis-status-detail">
                        {contact.date_dernier_statut
                          ? `maj le ${formatDateTime(contact.date_dernier_statut)}`
                          : `reçu le ${formatDate(contact.date_soumission)}`}
                        {contact.dernier_traite_par ? ` · ${contact.dernier_traite_par}` : ''}
                      </div>
                      <div className="demande-carte-recu">
                        Reçu le {formatDate(contact.date_soumission)}
                      </div>

                      {/* La colonne DOCUMENTS a disparu : il ne reste ici que
                          ce qui appelle un geste — des pièces encore
                          attendues, et le rappel pour les réclamer. */}
                      {contact.nb_documents_attendus > 0 &&
                        contact.nb_documents_recus < contact.nb_documents_attendus && (
                          <div
                            className="demande-carte-pieces"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span
                              className={`devis-status-pill ${classeDocuments(contact)}`}
                              title={`${contact.nb_documents_recus} pièce${
                                contact.nb_documents_recus > 1 ? 's' : ''
                              } reçue${contact.nb_documents_recus > 1 ? 's' : ''} sur ${
                                contact.nb_documents_attendus
                              } attendue${contact.nb_documents_attendus > 1 ? 's' : ''}`}
                            >
                              Pièces {contact.nb_documents_recus} / {contact.nb_documents_attendus}
                            </span>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-rappel-pieces"
                              onClick={() => handleRappelDocuments(contact)}
                              disabled={rappelId === contact.id}
                              title="Envoyer au client un email listant les pièces manquantes"
                            >
                              {rappelId === contact.id ? 'Envoi' : 'Rappel'}
                            </button>
                          </div>
                        )}
                    </div>

                    <div className="demande-carte-actions" onClick={(e) => e.stopPropagation()}>
                      {/* Trois rangées, de la plus engageante à la plus
                          discrète : l'action attendue en pleine largeur,
                          consulter ensuite, modifier / supprimer en dernier. */}
                      <div className="devis-actions-pile demande-actions">
                        {contact.devis_id ? (
                          // Devis déjà généré : le premier bouton mène à la
                          // fiche du devis, où il se relit puis se valide. Une
                          // fois validé, il est figé : il n'y a plus qu'à le
                          // consulter. Œil : le document tel qu'il part ;
                          // flèche : son téléchargement.
                          <>
                            <button
                              type="button"
                              className={`devis-btn devis-btn-sm devis-action-large${
                                contact.devis_valide_le ? '' : ' devis-btn-primary'
                              }`}
                              onClick={() => handleOuvrirDevis(contact.id)}
                              disabled={ouvertureId === contact.id}
                              title={contact.devis_numero || undefined}
                            >
                              {ouvertureId === contact.id
                                ? 'Ouverture'
                                : contact.devis_valide_le
                                  ? 'Fiche devis'
                                  : 'Valider'}
                            </button>
                            {/* Le PDF du dossier, dit en toutes lettres : un devis une
                                fois validé, une estimation tant qu'il ne l'est pas. */}
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier devis-action-large"
                              onClick={() => setApercuDevis(contact)}
                              title={`Voir le PDF ${contact.devis_valide_le ? 'du devis' : 'de l’estimation'} ${contact.devis_numero || ''}`.trim()}
                            >
                              <IconeOeil />
                              {`Voir PDF ${contact.devis_valide_le ? 'devis' : 'estimation'}`}
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier devis-action-large"
                              onClick={() => handlePdfDevis(contact.devis_id)}
                              disabled={pdfId === contact.devis_id}
                              title={`Télécharger le PDF ${contact.devis_valide_le ? 'du devis' : 'de l’estimation'} ${contact.devis_numero || ''}`.trim()}
                            >
                              {pdfId === contact.devis_id ? (
                                'En cours'
                              ) : (
                                <>
                                  <IconeTelecharger />
                                  {`Télécharger PDF ${contact.devis_valide_le ? 'devis' : 'estimation'}`}
                                </>
                              )}
                            </button>
                            {/* Le prochain document du dossier : le contrat, puis la
                                convocation, la feuille de présence… au choix. */}
                            <GenererSuivant
                              devisId={contact.devis_id}
                              contratId={contact.contrat_id}
                              parDefaut={
                                !contact.contrat_id
                                  ? 'contrat'
                                  : Number(contact.nb_convocations) > 0
                                    ? 'feuille_presence'
                                    : 'convocation'
                              }
                              onGenere={() => setReload((n) => n + 1)}
                            />
                          </>
                        ) : (
                          // Deux voies : l'IA, ou la main. La seconde n'attend
                          // pas l'échec de la première — si le service d'IA est
                          // en panne ou lit mal la demande, on compose le devis
                          // tout de suite.
                          <>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-action-large"
                              onClick={() => handleGenererDevis(contact.id)}
                              disabled={generationId === contact.id}
                            >
                              {generationId === contact.id ? 'En cours' : 'Générer'}
                            </button>
                            <div className="devis-actions-rangee">
                              <button
                                type="button"
                                className="devis-btn devis-btn-sm"
                                onClick={() => setSaisieManuelle({ contact, raison: null })}
                                disabled={generationId === contact.id}
                                title="Composer le devis à la main, sans passer par l'IA"
                              >
                                Manuel
                              </button>
                            </div>
                          </>
                        )}

                        <div className="devis-actions-rangee">
                          {/* Le détail de la demande : ce que le clic sur la
                              carte ouvre déjà, mais un bouton se voit. */}
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={() => setSelectedId(contact.id)}
                            title="Voir le détail de la demande"
                          >
                            <IconeOeil />
                            Voir
                          </button>
                          {/* Modifier : la demande tant qu'elle n'a pas de
                              devis, le devis ensuite (la demande est alors
                              figée). */}
                          <ActionsLigne
                            onModifier={() =>
                              contact.devis_id
                                ? handleOuvrirDevis(contact.id, true)
                                : setDemandeModifiee(contact)
                            }
                            modifierTitre={
                              contact.devis_id
                                ? 'Modifier le devis de cette demande'
                                : 'Modifier la demande'
                            }
                            modifierDisabled={ouvertureId === contact.id}
                            onSupprimer={() => setSuppression(contact)}
                            supprimerTitre="Supprimer la demande (ses devis, contrats et documents restent)"
                            supprimerDisabled={suppressionEnCours || generationId === contact.id}
                          />
                        </div>
                        {!['sans_suite', 'devis_refuse', 'termine'].includes(
                          contact.dernier_statut
                        ) && (
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-danger devis-action-large"
                            onClick={() => handleSansSuite(contact)}
                            disabled={sansSuiteId === contact.id}
                            title="Clore la demande : plus de suite à donner"
                          >
                            {sansSuiteId === contact.id ? 'En cours' : 'Sans suite'}
                          </button>
                        )}
                      </div>
                      {generationErreur?.contactId === contact.id && (
                        <div className="devis-row-erreur">
                          Génération impossible (détail en haut de l’écran).
                        </div>
                      )}
                    </div>
                  </div>

                  {/* L'avancement occupe le bas de la carte, sur toute sa
                      largeur : les sept étapes ont besoin de la place pour
                      être nommées. */}
                  <div className="demande-carte-avancement" onClick={(e) => e.stopPropagation()}>
                    <Avancement contact={contact} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {rangeStart}-{rangeEnd} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId != null && (
        <ContactDetailPanel
          contact={selectedContact}
          loading={detailLoading}
          error={detailError}
          onClose={() => setSelectedId(null)}
          devis={devisContact}
          devisLoading={devisLoading}
          generation={generationId === selectedId}
          generationErreur={
            generationErreur?.contactId === selectedId ? generationErreur.message : null
          }
          onGenererDevis={() => handleGenererDevis(selectedId)}
          onVoirDevis={() => setDevisOuvert(devisContact)}
        />
      )}

      {devisOuvert && (
        <DevisPanel
          devis={devisOuvert}
          editionInitiale={devisEnEdition}
          onClose={() => {
            setDevisOuvert(null);
            setDevisEnEdition(false);
          }}
          onDevisChange={(maj) => {
            setDevisOuvert(maj);
            setReload((n) => n + 1);
            if (selectedId === maj.contact_id) setDevisContact(maj);
          }}
          onValide={(valide) => {
            // Devis figé : on quitte l'aperçu du devis pour le détail de la
            // demande, où le PDF apparaît désormais en pièce jointe.
            setDevisOuvert(null);
            setReload((n) => n + 1);
            setDevisContact(valide);
            setSelectedId(valide.contact_id);
          }}
        />
      )}

      {attente && (
        <div className="devis-modal-overlay">
          <div className="devis-modal devis-modal-attente">
            {attente.etat === 'encours' ? (
              <>
                <div className="devis-attente-titre">Génération du devis en cours…</div>
                <p className="devis-attente-texte">
                  Demande de <strong>{attente.contact.societeNom}</strong> créée ; le devis est
                  en cours de rédaction.
                </p>
                <div className="devis-attente-barre">
                  <span />
                </div>
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => setAttente(null)}
                >
                  Continuer sans attendre
                </button>
              </>
            ) : (
              <>
                <div className="devis-attente-titre">Génération automatique impossible</div>
                <p className="devis-attente-texte">{attente.motif}</p>
                <p className="devis-attente-texte">
                  La demande reste «&nbsp;à traiter&nbsp;». Composez le devis à la main pour
                  poursuivre.
                </p>
                <div className="devis-attente-actions">
                  <button
                    type="button"
                    className="devis-btn"
                    onClick={() => setAttente(null)}
                  >
                    Plus tard
                  </button>
                  <button
                    type="button"
                    className="devis-btn devis-btn-primary"
                    onClick={() => {
                      setSaisieManuelle({
                        contact: attente.contact,
                        raison: attente.motif,
                      });
                      setAttente(null);
                    }}
                  >
                    Créer le devis manuellement
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {importOuvert && (
        <ImportTrelloPanel
          onClose={() => setImportOuvert(false)}
          onImporte={(resume) => {
            if (resume.importes > 0) {
              setPage(1);
              setReload((n) => n + 1);
            }
          }}
        />
      )}

      {depuisTexte && (
        <CreerDepuisTextePanel
          entite="demande"
          onClose={() => setDepuisTexte(false)}
          onCree={(res) => {
            setPage(1);
            setReload((n) => n + 1);
            setRappelMessage({ type: 'succes', texte: res.message || 'Demande créée.' });
          }}
        />
      )}

      {nouvelleDemande && (
        <NouvelleDemandePanel
          onClose={() => setNouvelleDemande(false)}
          onCree={(contact) => {
            setNouvelleDemande(false);
            setPage(1);
            setReload((n) => n + 1);
            // La génération démarre côté serveur : on suit son aboutissement.
            setAttente({ contact, etat: 'encours' });
          }}
        />
      )}

      {/* L'erreur de génération s'explique ici, en clair, plutôt que sur les
          220 px de la cellule : le service d'IA saturé, la clé refusée, une
          réponse inexploitable… avec ce qu'il faut faire. */}
      {/* Rappel des pièces à fournir : succès comme échec se disent ici,
          l'action est déclenchée depuis une cellule trop étroite pour un
          message. */}
      {rappelMessage && (
        <Notification
          type={rappelMessage.type}
          titre={
            rappelMessage.titre ||
            (rappelMessage.type === 'succes' ? 'Rappel envoyé' : 'Rappel impossible')
          }
          message={rappelMessage.texte}
          onFermer={() => setRappelMessage(null)}
          dureeMs={rappelMessage.type === 'succes' ? 6000 : null}
        />
      )}

      {apercuDevis && (
        <PanneauApercu
          titre={`${apercuDevis.devis_valide_le ? 'Devis' : 'Estimation'}${apercuDevis.devis_numero ? ` ${apercuDevis.devis_numero}` : ''}`}
          sousTitre={[apercuDevis.societe_nom, nomDemande(apercuDevis)]
            .filter(Boolean)
            .join(' · ')}
          url={`${urlPdfDevis(apercuDevis.devis_id, { apercu: true })}&v=${versionApercu}`}
          onClose={() => setApercuDevis(null)}
          actions={
            <>
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => regenererPdf(apercuDevis)}
                disabled={regenId === apercuDevis.devis_id || apercuDevis.devis_origine === 'externe'}
                title={
                  apercuDevis.devis_origine === 'externe'
                    ? 'Devis établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                    : 'Refaire le PDF à partir des données actuelles'
                }
              >
                {regenId === apercuDevis.devis_id ? 'Régénération' : 'Régénérer le PDF'}
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-fichier"
                onClick={() => handlePdfDevis(apercuDevis.devis_id)}
                disabled={pdfId === apercuDevis.devis_id}
              >
                {pdfId === apercuDevis.devis_id ? 'En cours' : (<><IconeTelecharger />{`Télécharger PDF ${apercuDevis.devis_valide_le ? 'devis' : 'estimation'}`}</>)}
              </button>
            </>
          }
        />
      )}

      {suppressionMessage && (
        <Notification
          type={suppressionMessage.type}
          titre={
            suppressionMessage.titre ||
            (suppressionMessage.type === 'succes' ? 'Demande mise à la corbeille' : 'Suppression impossible')
          }
          message={suppressionMessage.texte}
          onFermer={() => setSuppressionMessage(null)}
          dureeMs={suppressionMessage.type === 'succes' ? 6000 : null}
        />
      )}

      {suppression && (
        <div
          className="devis-modal-overlay"
          onClick={suppressionEnCours ? undefined : () => setSuppression(null)}
        >
          <div
            className="devis-modal devis-modal-etroit"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-labelledby="suppression-titre"
          >
            <div className="devis-modal-header">
              <div>
                <div className="devis-modal-numero" id="suppression-titre">
                  Supprimer cette demande ?
                </div>
                <div className="devis-modal-sub">{nomDemande(suppression)}</div>
              </div>
              <div className="devis-modal-header-right">
                <button
                  type="button"
                  className="devis-drawer-close"
                  onClick={() => setSuppression(null)}
                  disabled={suppressionEnCours}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="devis-modal-body">
              <div className="devis-alert devis-alert-erreur">
                <strong>Mise à la corbeille</strong> (restaurable pendant 30 jours) : la demande
                seule.
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  <li>
                    Le devis{suppression.devis_numero ? ` ${suppression.devis_numero}` : ''}, le
                    contrat, les journées, participants, convocations, feuilles de présence,
                    pièces déposées et rendez-vous <strong>restent</strong>, rattachés à cette
                    demande supprimée.
                  </li>
                </ul>
                <div style={{ marginTop: 6 }}>
                  La fiche client est conservée, ainsi que tous les fichiers produits (rien n’est
                  effacé du dossier de stockage). Corbeille : menu « Corbeille ».
                </div>
              </div>
              <div className="devis-modal-actions">
                <button
                  type="button"
                  className="devis-btn"
                  onClick={() => setSuppression(null)}
                  disabled={suppressionEnCours}
                >
                  Non, garder
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-danger"
                  onClick={handleSupprimer}
                  disabled={suppressionEnCours}
                  autoFocus
                >
                  {suppressionEnCours ? 'Suppression…' : 'Oui, supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {generationErreur && (
        <Notification
          type="erreur"
          titre="Génération du devis impossible"
          message={generationErreur.message.replace(/^Génération du devis impossible\.?\s*/, '')}
          onFermer={() => setGenerationErreur(null)}
        />
      )}

      {demandeModifiee && (
        <NouvelleDemandePanel
          demande={demandeModifiee}
          onClose={() => setDemandeModifiee(null)}
          onCree={() => {
            setDemandeModifiee(null);
            setRappelMessage({ type: 'succes', texte: 'Demande mise à jour.' });
            setReload((n) => n + 1);
          }}
        />
      )}

      {saisieManuelle && (
        <DevisManuelPanel
          contact={saisieManuelle.contact}
          raison={saisieManuelle.raison}
          onClose={() => setSaisieManuelle(null)}
          onCree={(devis) => {
            setSaisieManuelle(null);
            setReload((n) => n + 1);
            if (selectedId === devis.contact_id) setDevisContact(devis);
            setDevisOuvert(devis);
          }}
        />
      )}

      {panneauEmail && (
        <ModeleEmailPanneau type={'devis'} onClose={() => setPanneauEmail(false)} />
      )}

      {apercuRappel && (
        <ApercuEmail
          titre="Relancer sur les pièces à fournir"
          sousTitre={apercuRappel.societe_nom || [apercuRappel.prenom, apercuRappel.nom].filter(Boolean).join(' ')}
          charger={() => apercuRappelDocuments(apercuRappel.id)}
          onEnvoyer={(edits) => envoyerRappelApresApercu(apercuRappel, edits)}
          onClose={() => setApercuRappel(null)}
        />
      )}
    </div>
  );
}

export default DemandeDevis;



